import { useState } from "react";
import { CheckSquare, CircleNotch, Trash, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface BulkActionsBarProps {
  totalCount: number;
  selectedCount: number;
  allSelected: boolean;
  selectionMode?: boolean;
  selectingDisabled?: boolean;
  deleting?: boolean;
  entityPluralLabel: string;
  onToggleSelectionMode?: (enabled: boolean) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onClearSelection: () => void;
  onConfirmDelete: () => Promise<void>;
}

export function BulkActionsBar({
  totalCount,
  selectedCount,
  allSelected,
  selectionMode = true,
  selectingDisabled = false,
  deleting = false,
  entityPluralLabel,
  onToggleSelectionMode,
  onToggleSelectAll,
  onClearSelection,
  onConfirmDelete,
}: BulkActionsBarProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (totalCount === 0) return null;

  if (!selectionMode && onToggleSelectionMode) {
    return (
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {totalCount} {entityPluralLabel}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 -mr-2 text-muted-foreground hover:text-foreground"
          onClick={() => onToggleSelectionMode(true)}
          disabled={deleting}
        >
          <CheckSquare className="h-3.5 w-3.5" />
          Selecionar
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-3 rounded-lg border border-border/60 bg-card/80 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {selectionMode ? (
          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={allSelected}
              onCheckedChange={(v) => onToggleSelectAll(Boolean(v))}
              disabled={selectingDisabled || deleting}
              aria-label="Selecionar todos os itens"
            />
            Selecionar todos ({totalCount})
          </label>
        ) : (
          <span className="text-xs text-muted-foreground">{totalCount} itens</span>
        )}

        <div className="flex items-center gap-2">
          {onToggleSelectionMode ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() => onToggleSelectionMode(!selectionMode)}
              disabled={deleting}
            >
              {selectionMode ? "Concluir seleção" : "Selecionar"}
            </Button>
          ) : null}

          {selectionMode ? (
            <span className="text-xs text-muted-foreground">
              {selectedCount} selecionado{selectedCount === 1 ? "" : "s"}
            </span>
          ) : null}

          {selectionMode ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={onClearSelection}
              disabled={selectedCount === 0 || deleting}
            >
              <X className="h-4 w-4" />
              Limpar
            </Button>
          ) : null}

          {selectionMode ? (
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  disabled={selectedCount === 0 || deleting}
                >
                  {deleting ? (
                    <CircleNotch className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash className="h-4 w-4" />
                  )}
                  Excluir selecionados
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir itens selecionados?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Você está prestes a excluir {selectedCount} {entityPluralLabel}. Esta ação é
                    permanente e não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={deleting}
                    onClick={async (e) => {
                      e.preventDefault();
                      await onConfirmDelete();
                      setConfirmOpen(false);
                    }}
                    className="gap-1.5"
                  >
                    {deleting ? (
                      <>
                        <CircleNotch className="h-4 w-4 animate-spin" />
                        Excluindo...
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-4 w-4" />
                        Confirmar exclusão
                      </>
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </div>
    </div>
  );
}
